<template>
  <div class="c-btns-container" v-if="!isSessionPendingRef">
    <CustomButton
      usage="button"
      type="submit"
      color="danger"
      :text="`${groupRolesRef?.includes(RoleEnumTypeV2.MENTOR) ? t('ui.button.cancel') : t('ui.button.decline')}`"
      @emit:click="cancelSession"
    />
    <CustomButton
      v-if="canEditSessionRef"
      usage="button"
      type="submit"
      color="primary"
      :text="t('ui.button.edit')"
      @emit:click="openEditSessionModal"
    >
      <template #icon>
        <EditIcon color="white" />
      </template>
    </CustomButton>
    <CustomButton usage="button" type="submit" color="primary" :text="t('ui.button.join')" @emit:click="joinMeeting">
      <template #icon>
        <CameraIcon color="white" />
      </template>
    </CustomButton>
  </div>
</template>

<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query'
import { computed } from 'vue'
import { toast } from 'vue-sonner'

import useAccount from '@/features/auth/hooks/use-account.hook'
import { RoleEnumTypeV2 } from '@/features/auth/types'
import useProgram from '@/features/programs/hooks/use-program.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import { V1UpdateSessionStatusMutation } from '@/features/sessions/api'
import useSession from '@/features/sessions/hooks/use-session.hook'
import { SessionStatusEnumType } from '@/features/sessions/types'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import CameraIcon from '@/features/ui/components/icons/camera-icon.vue'
import EditIcon from '@/features/ui/components/icons/edit-icon.vue'
import { selectGetSessionDetailsModal } from '@/features/ui/store/modal/modal.selectors'
import type { ConfirmActionModalType, EditSessionModalType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useGroupRoles from '@/features/users/hooks/use-group-roles.hook'
import useRouter from '@/hooks/use-router.hook'
import { useI18n } from '@/lib/i18n'
import { invalidateSession, invalidateSessions } from '@/utils/lib/vue-query'
const router = useRouter()
const { t } = useI18n()

const { accountRef } = useAccount()

const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const { programRef } = useProgram(selectedProgramIdRef)

const sessionDetailsModalRef = selectGetSessionDetailsModal(modalsStore)

const sessionIdRef = computed(() => sessionDetailsModalRef.value.attachedData.sessionId ?? null)
const { sessionRef, isPendingRef: isSessionPendingRef } = useSession(sessionIdRef)

const groupIdRef = computed(() => sessionRef.value?.mentoringGroupId)
const { groupRolesRef } = useGroupRoles({ groupIdRef })

const canEditSessionRef = computed(
  () =>
    groupRolesRef?.value?.includes(RoleEnumTypeV2.MENTOR) ||
    (groupRolesRef?.value?.includes(RoleEnumTypeV2.MENTEE) && programRef.value?.settings.menteeCanBookSession),
)

function joinMeeting() {
  if (!sessionRef.value) return
  modalsStore.toggleModal('session-details', false)
  router.push({ query: { ...router.currentRoute.query, meetingRoomId: sessionRef.value.meetingRoomId } })
}

function openEditSessionModal() {
  modalsStore.toggleModal('session-details', false)
  modalsStore.toggleModal('edit-session', true)
  modalsStore.updateModalAttachedData<EditSessionModalType>('edit-session', {
    sessionId: sessionIdRef.value!,
  })
}

// update the session status
const { mutate: updateSessionStatus } = useMutation({
  mutationFn: V1UpdateSessionStatusMutation,
  onSuccess() {
    modalsStore.toggleModal('session-details', false)
    invalidateSessions()
    invalidateSession(sessionIdRef.value!)

    if (groupRolesRef?.value?.includes(RoleEnumTypeV2.MENTOR)) {
      toast?.success(t('session.cancel.success'))
    } else {
      toast?.success(t('session.decline.success'))
    }
  },
  onError() {
    modalsStore.toggleModal('session-details', false)
    toast?.error(t('error.common'))
  },
})

function cancelSession() {
  modalsStore.toggleModal('confirm-action', true)
  modalsStore.updateModalAttachedData<ConfirmActionModalType>('confirm-action', {
    title: t('session.cancel.confirmation.title'),
    content: t('ui.modal.confirmation.irreversible'),
    cancelFn() {
      modalsStore.toggleModal('confirm-action', false)
    },
    fn() {
      if (sessionRef.value?.id && accountRef.value?.id) {
        updateSessionStatus({
          sessionId: sessionRef.value.id,
          status: SessionStatusEnumType.CANCELLED,
        })
      } else {
        toast?.error(t('error.common'))
        throw new Error('Session or account not found')
      }
    },
  })
}
</script>
